import React, { useEffect, useState } from "react";
import { Button, Dialog, Divider, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { branchTimezone } from "../../../utils/summaryCalculation";
import { post_Refund } from "../../../services/refund";
import useAuth from "../../../components/hooks/useAuth";
import useConfirm from "../../../components/hooks/useConfirm";
import CustomFormatDatePicker from "../../../components/common/CustomFormatDatePicker";
import { useTheme } from "../../../components/hooks/useTheme";
import { validateMin } from "../../../utils/validateMin";
import RefundDetailsPopup from "./RefundDetailsPopup2";
import colorConfigs from "../../../configs/colorConfigs";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";
import useAccessControl from "../../../components/hooks/useAccessControl";
import moment from "moment";
import useGeneral from "../../../components/hooks/useGeneral";

type Props = {
  summaryData: any;
  formattedCreatedAt: string;
  refreshTheSummary: () => void;
  refundList: any[];
  refreshThRefund: () => void;
  isCompletedOrCancelled: boolean;
  setPleaseWait: (value: boolean) => void;
  reservationId: number;
  preferredDateFormat: string;
};

export default function AddRefund({
  summaryData,
  formattedCreatedAt,
  refreshTheSummary,
  refundList,
  refreshThRefund,
  isCompletedOrCancelled,
  setPleaseWait,
  reservationId,
  preferredDateFormat
}: Props) {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { theme, themeStyles } = useTheme();
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const { dateFormat }: any = useGeneral();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const { permissions }: any = useAccessControl();
  const [offlineRefundPopup, setOfflineRefundPopup] = useState<boolean>(false);
  const [refundMethod, setRefundMethod] = useState<string>("Bank Transfer");
  const [refundDate, setRefundDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [refundAmount, setRefundAmount] = useState<number>(0.0);
  const [refundReference, setRefundReference] = useState<string>("");
  const [isRefundSendEmail, setIsRefundSendEmail] = useState<boolean>(false);
  const [particularRefundDetails, setParticularRefundDetails] = useState<any | null>(null);
  const [openRefundDetailsPopup, setOpenRefundDetailsPopup] = useState<boolean>(false);
  const UpdateTimezone = branchTimezone(formattedCreatedAt);
  const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

  useEffect(() => {
    if (UpdateTimezone) {
      setRefundDate(moment(new Date(UpdateTimezone)).format("YYYY-MM-DD"));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetTheOfflineRefund = () => {
    setRefundMethod("Bank Transfer");
    setRefundAmount(0.0);
    setIsRefundSendEmail(false);
    setRefundDate(moment(new Date(UpdateTimezone)).format("YYYY-MM-DD"));
    setRefundReference("");
    setOfflineRefundPopup(false);
  }

  const handleRefundPayment = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!isCompletedOrCancelled || summaryData?.status === "Cancelled" || summaryData?.status === "Cancelled Paid") {
      e.preventDefault();

      if (refundAmount === 0) {
        setConfirmData({
          title: 'Warning',
          summary: "You cannot proceed because the refund amount is 0.",
          confirmBtn: 'Ok',
          isConfirm: (callback: any) => {
            callback();
          }
        });

        setConfirmPopup(true);
        return;
      }

      setPleaseWait(true);

      await post_Refund({
        reservationId: reservationId,
        refundType: "Refund",
        dateofRefund: refundDate,
        amount: refundAmount,
        reference: refundReference,
        createdAt: new Date(UpdateTimezone).toLocaleString(),
        createdBy: auth.givenname,
        syncedWithAccounting: false,
        refundMethod: refundMethod,
        refundStatus: "Approved"
      }, auth?.id, tenantId)
        .then(() => {
          refreshTheSummary();
          refreshThRefund();
          setOfflineRefundPopup(false);
          handleTenantError(null);
        })
        .catch((error) => {
          if (error?.response?.data?.includes("Declined")) {
            refreshThRefund();
          }

          setConfirmData({
            title: 'Warning',
            summary: error?.response?.data,
            confirmBtn: 'Ok',
            isConfirm: (callback: any) => {
              callback();
            }
          });

          setConfirmPopup(true);

          setOfflineRefundPopup(false);
          setPleaseWait(false);
          handleTenantError(error);
        });
    }
  }

  useEffect(() => {
    if (summaryData) {
      let remainigRefundAmount = 0;
      remainigRefundAmount = summaryData?.outstandingBalance < 0 ? Math.abs(summaryData?.outstandingBalance) : 0;

      setRefundAmount(remainigRefundAmount <= 0 ? 0 : remainigRefundAmount);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData]);

  return (
    <div className="my-4">
      <span className={`text-[14px] font-semibold inline-block mb-2 ${themeStyles.main.textHeadingColor}`}>
        Refunds
      </span>

      <Divider className={`w-full ${themeStyles.divider.bg}`} />

      <div className="w-full my-2">
        <table className="min-w-full">
          <tbody className="text-left">
            <tr className={`border-b ${themeStyles.main.borderColor}`}>
              <th className={`w-[5%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>#</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Refund Type</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Date</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Amount</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Reference</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Created at</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Created by</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Synced with Accounting?</th>
            </tr>

            {
              refundList?.map((item: any, index: number) => {
                return (
                  <tr key={index} className={`border-b ${themeStyles.main.borderColor}`}>
                    <td className="py-2 w-[8%]">
                      <span
                        className={`text-xs font-medium ${(permissions?.disabledrefund || auth?.roles?.toLowerCase()?.trim() === "admin") ? "text-blue-500 underline cursor-pointer" : "cursor-default"}r ${themeStyles.main.textHeadingColor}`}
                        onClick={() => {
                          if (permissions?.disabledrefund || auth?.roles?.toLowerCase()?.trim() === "admin") {
                            setOpenRefundDetailsPopup(true);
                            setParticularRefundDetails(item);
                          }
                        }}
                      >
                        {item?.id}
                      </span>
                    </td>

                    <td className="py-2 w-[10%]">
                      <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>{item?.refundType}</span>
                    </td>

                    <td className="w-[10%]">
                      <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>
                        {
                          item?.dateofRefund
                            ? moment(
                              new Date(
                                item?.dateofRefund
                                  ?.replaceAll("-", "/")
                                  ?.split("T")[0]
                              )
                            ).format(dateFormat)
                            : ""
                        }
                      </span>
                    </td>

                    <td className="py-2 w-[10%]">
                      <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>${parseFloat(item?.amount)?.toFixed(2)}</span>
                    </td>

                    <td className="py-2 w-[10%]">
                      <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>{item?.reference}</span>
                    </td>

                    <td className="py-2 w-[10%]">
                      <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>
                        {
                          item?.createdAt
                            ? moment(new Date(item.createdAt)).format(dateFormat + " hh:mm a")
                            : ""
                        }
                      </span>
                    </td>

                    <td className="py-2 w-[10%]">
                      <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>{item?.createdBy}</span>
                    </td>

                    <td className="py-2 w-[10%]">
                      <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>{item?.syncedWithAccounting ? "Yes" : "No"}</span>
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>

        <div className="w-full flex items-center gap-2.5 mt-4">
          {
            (
              (permissions?.disabledrefund || auth?.roles?.toLowerCase()?.trim() === "admin")
            ) && (
              <Button variant="contained"
                sx={{
                  backgroundColor: colorConfigs.btn.main.bg,
                  fontSize: "10px"
                }}
                onClick={() => {
                  if (!isCompletedOrCancelled || summaryData?.status === "Cancelled" || summaryData?.status === "Cancelled Paid") {
                    setOfflineRefundPopup(true);
                  }
                }}
              >
                Add Offline Refund
              </Button>
            )
          }
        </div>
      </div>

      <Dialog
        open={offlineRefundPopup}
        maxWidth="md"
        fullScreen={false}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '600px',
            width: '100%',
            borderRadius: '16px',
            overflowY: "unset",
          },
        }}
      >
        <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320.591 320.591"
            className={`w-3 cursor-pointer float-right ${closeIconFill}`}
            onClick={resetTheOfflineRefund}
          >
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
          </svg>
          <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
            <div className="w-full mt-2">
              <div className={`w-full`}>
                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                  <div className="flex items-center gap-1.5">
                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Add Offline Refund</h2>
                  </div>
                </div>

                <div className="mt-2 p-2">
                  <div className="w-full">

                    <form onSubmit={(e) => handleRefundPayment(e)}>
                      <p className="text-[10px] mt-2 bg-[rgb(253,248,227)] text-[#8a6d3b] text-left rounded-sm py-2 pl-4 pr-6">
                        By Creating a refund here, it won't return any money to customer.
                        Create refunds here only if you processed this refund manually.
                      </p>

                      <div className="w-full mt-2">
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Refund Type</label>
                              <select
                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                value={refundMethod}
                                onChange={(e) => setRefundMethod(e.target.value)}
                                required
                              >
                                <option className={`${themeStyles.main.tailwindSectionBg}`} value="Bank Transfer">Bank Transfer</option>
                                <option className={`${themeStyles.main.tailwindSectionBg}`} value="Cash">Cash</option>
                                <option className={`${themeStyles.main.tailwindSectionBg}`} value="Check">Check</option>
                              </select>
                            </div>

                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Amount</label>
                              <input
                                type="number"
                                step={0.01}
                                min={0}
                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                value={refundAmount}
                                onChange={(e) =>
                                  setRefundAmount(validateMin(e.target.value, 0))
                                }
                                required
                              />
                            </div>

                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Send Confirmation Email</label>
                              <RadioGroup
                                row
                                value={isRefundSendEmail ? "Yes" : "No"}
                                onChange={(e) =>
                                  setIsRefundSendEmail(
                                    e.target.value === "Yes" ? true : false
                                  )
                                }
                              >
                                <FormControlLabel
                                  value="Yes"
                                  control={
                                    <Radio
                                      sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                          color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 14,
                                        },
                                      }}
                                    />
                                  }
                                  label="Yes"
                                  sx={{
                                    color: themeStyles.form.labelTextColor,
                                    fontSize: "12px"
                                  }}
                                />

                                <FormControlLabel
                                  value="No"
                                  control={
                                    <Radio
                                      sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                          color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 14,
                                        },
                                      }}
                                    />
                                  }
                                  label="No"
                                  sx={{
                                    color: themeStyles.form.labelTextColor,
                                  }}
                                />
                              </RadioGroup>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Date</label>
                              <CustomFormatDatePicker
                                value={refundDate}
                                onChange={setRefundDate}
                                dateFormat={preferredDateFormat}
                                required={true}
                              />
                            </div>

                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Reference</label>
                              <input
                                type="text"
                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                value={refundReference}
                                onChange={(e) => setRefundReference(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4 mt-6">
                        <Button variant="contained"
                          sx={{
                            backgroundColor: colorConfigs.btn.delete.bg
                          }}
                          type="submit"
                        >
                          Save
                        </Button>

                        <Button variant="contained"
                          sx={{
                            backgroundColor: colorConfigs.btn.cancel.bg,
                            border: colorConfigs.btn.cancel.border,
                            color: colorConfigs.btn.cancel.color
                          }}
                          onClick={resetTheOfflineRefund}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {
        (permissions?.disabledrefund || auth?.roles?.toLowerCase()?.trim() === "admin")
        && (
          <RefundDetailsPopup
            open={openRefundDetailsPopup}
            onClose={() => setOpenRefundDetailsPopup(false)}
            refundDetails={particularRefundDetails}
            reservationId={reservationId}
            setPleaseWait={setPleaseWait}
            isCompletedOrCancelled={isCompletedOrCancelled}
            refreshTheSummary={() => {
              refreshTheSummary();
              refreshThRefund();
            }}
          />
        )
      }
    </div>
  );
}